import type {
  CallNotesConfig,
  CallTool,
  CallTriggerConfig,
  CustomNodeType,
  TriggerKind,
} from '@/types/custom-playbooks';

export const CALL_TOOL_LABEL: Record<CallTool, string> = {
  fireflies: 'Fireflies',
  fathom: 'Fathom',
  gong: 'Gong',
};
export const CALL_TRIGGER_KIND: Record<CallTool, TriggerKind> = {
  fireflies: 'fireflies_call',
  fathom: 'fathom_call',
  gong: 'gong_call',
};
export const CALL_NOTES_STEP: Record<CallTool, CustomNodeType> = {
  fireflies: 'fireflies_notes',
  fathom: 'fathom_notes',
  gong: 'gong_notes',
};

export function callToolOfStep(type: CustomNodeType): CallTool | null {
  if (type === 'fireflies_notes') return 'fireflies';
  if (type === 'fathom_notes') return 'fathom';
  if (type === 'gong_notes') return 'gong';
  return null;
}

/** A call trigger's settings, all optional: missing reads as the defaults. */
export function callTriggerReady(
  config: CallTriggerConfig | undefined
): boolean {
  if (config === undefined) return true;
  if (!config || typeof config !== 'object' || 'tools' in config) return false;
  if (
    config.who !== undefined &&
    !['external_participants', 'first_external_participant'].includes(
      config.who
    )
  )
    return false;
  if (
    config.backfill !== undefined &&
    !['now', 'historic'].includes(config.backfill)
  )
    return false;
  for (const [value, min, max] of [
    [config.maxPerMeeting ?? 5, 1, 10],
    [config.minDurationMinutes ?? 10, 0, 240],
  ])
    if (!Number.isInteger(value) || value < min || value > max) return false;
  const titles = config.titleContains ?? [];
  return (
    Array.isArray(titles) &&
    titles.length <= 10 &&
    titles.every(
      (title) =>
        typeof title === 'string' &&
        !!title.trim() &&
        title.trim().length <= 60 &&
        !title.includes('\0')
    )
  );
}

/** Get call notes: the latest call, or the latest in 1..365 days. */
export function callNotesReady(config: CallNotesConfig | undefined): boolean {
  const recency = config?.recency ?? 'latest';
  if (recency === 'latest') return config?.withinDays === undefined;
  if (recency !== 'within_days') return false;
  const days = config?.withinDays;
  return Number.isInteger(days) && days! >= 1 && days! <= 365;
}

export function callNotesSummary(config: CallNotesConfig | undefined): string {
  const days = config?.withinDays ?? 0;
  const summary =
    config?.recency !== 'within_days'
      ? 'Latest call'
      : `Latest call in ${days} day${days === 1 ? '' : 's'}`;
  return config?.transcript === true ? `${summary}, with transcript` : summary;
}
